import { useState, useEffect } from "react";
import getEnv from "../../../services/util/getEnv";
import {
  COMPARE_STATUS,
  STATUS,
} from "../../legacy_components/constants/status";
import { TestState } from '../../legacy_components/UI_V2_Components/tests/constants';

const testPassedV1 = test => test.passed && test.state === TestState.COMPLETED;

const testPassed = test => test.passed === 1 && test.submit_time !== null;

export const techStacksPassed = (tests, mcq_use_apiv1) => {
  const { stackDemands = [], challenges = [] } = tests || {};
  
  let passedChallenges = [];

  /**
   * Use `testPassedV1` method once `mcq_use_apiv1` is rolled out to 100% of users.
   */
  if (mcq_use_apiv1) {
    passedChallenges = challenges
      .filter(challenge => testPassedV1(challenge))
      .map(({ challenge_id }) => challenge_id);
  } else {
    passedChallenges = challenges
      .filter(challenge => testPassed(challenge))
      .map(({ challenge_id }) => challenge_id);
  }

  let stacksPassed = [];
  for (let stack of stackDemands) {
    const { groups } = stack;
    const [{ listMCQ }] = groups;
    const noOfPassedInStack = listMCQ.reduce((count, challenge_id) => {
      if (passedChallenges.includes(challenge_id)) {
        count++;
      }
      return count;
    }, 0);
    if (noOfPassedInStack === listMCQ.length) {
      if (+stack.stack_demand_id === +getEnv("REACT_APP_TECHLEAD_STACK_ID"))
        continue;
      stacksPassed = [...stacksPassed, stack];
    }
  }
  return stacksPassed;
};


export const getCurrentActiveStack = (tests) => {
  let currentStackId = null;
  try {
    const { activeTab = null } = JSON.parse(
      localStorage.getItem(MOD_LOCALSTORAGE_KEY)
    );
    currentStackId = +activeTab.split(":")[0];
  } catch (e) {
    return null;
  }
  const { stackDemands = [] } = tests || {};
  const stack = stackDemands.find(
    ({ stack_demand_id }) => stack_demand_id === currentStackId
  );
  if (!stack) return null;
  return stack;
}

/**
 * Remove `getNextChallengeFromCurrentStack` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromCurrentStack = (stack, tests) => {
  const challengeIds = stack.groups[0].listMCQ;
  const { challenges = [] } = tests || {};
  const eligibleChallenges = challenges
    .filter(({ challenge_id }) => challengeIds.includes(challenge_id))
    .filter(({ passed }) => passed === 3);

  if (!eligibleChallenges.length) return null;

  const nextChallenge  = eligibleChallenges.reduce((next, current) => {
    if (next.YoE > current.YoE) {
      return next;
    }
    return current;
  });

  return nextChallenge;
}

/**
 * Use `getNextChallengeFromCurrentStackV1` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromCurrentStackV1 = (stack, tests) => {
  const challengeIds = stack.groups[0].listMCQ;
  const { challenges = [] } = tests || {};
  const eligibleChallenges = challenges
    .filter(({ challenge_id }) => challengeIds.includes(challenge_id))
    .filter(
      ({ state }) => state === TestState.TODO || state === TestState.IN_PROGRESS
    );

  if (!eligibleChallenges.length) return null;

  const nextChallenge = eligibleChallenges.reduce((next, current) => {
    if (next.YoE > current.YoE) {
      return next;
    }
    return current;
  });

  return nextChallenge;
};

/**
 * Remove `getNextChallengeFromAllAvailableStack` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromAllAvailableStack = (tests) => {
  const { challenges = [] } = tests || {};
  const eligibleChallenges = challenges
    .filter(({ passed }) => passed === 3);

  if (!eligibleChallenges.length) return null;

  const nextChallenge  = eligibleChallenges.reduce((next, current) => {
    if (next.YoE > current.YoE) {
      return next;
    }
    return current;
  });

  return nextChallenge;
}

/**
 * Use `getNextChallengeFromAllAvailableStackV1` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromAllAvailableStackV1 = tests => {
  const { challenges = [] } = tests || {};
  const eligibleChallenges = challenges.filter(
    ({ state }) => state === TestState.TODO || state === TestState.IN_PROGRESS
  );

  if (!eligibleChallenges.length) return null;

  const nextChallenge = eligibleChallenges.reduce((next, current) => {
    if (next.YoE > current.YoE) {
      return next;
    }
    return current;
  });

  return nextChallenge;
};

/**
 * Remove `getNextChallengeFromAllAvailableUnfailedStack` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromAllAvailableUnfailedStack = (tests) => {
    // Pre-cache challenge info
    const challengesInfoMapped = {};
    const { challenges = [], stackDemands = [] } = tests || {};
    challenges.map((challenge) => {
      if (!challengesInfoMapped[challenge.challenge_id]) {
        challengesInfoMapped[challenge.challenge_id] = challenge;
      }
    })

    // First find out tech stacks that don't have any failed tests
    // List all the challenges of those tech stacks
    // Filter out the passed challenges
    // Take the challenge with most YoE
    try {
      const nextChallenge = stackDemands.filter((stack) => {
        const challenges = stack.groups[0].listMCQ;
        for(let challengeId of challenges) {
          if (challengesInfoMapped[challengeId].passed === 2) {
            return false;
          }
        }
        return true;
      })
      .reduce((acc, stack) => {
        const challenges = stack.groups[0].listMCQ;
        for (let challenge of challenges) {
           acc = !acc.includes(challenge) ? [...acc, challenge] : acc;
        }
        return acc;
      }, [])
      .map((challengeId) => challengesInfoMapped[challengeId])
      .filter(({ passed }) => passed !== 1)
      .reduce((highest, current) => highest.YoE > current.YoE ? highest : current);

      return nextChallenge;
    } catch (e) {
      return null;
    }
}

/**
 * Use `getNextChallengeFromAllAvailableUnfailedStackV1` method once `mcq_use_apiv1` is rolled out to 100% of users.
 */
export const getNextChallengeFromAllAvailableUnfailedStackV1 = tests => {
  // Pre-cache challenge info
  const challengesInfoMapped = {};
  const { challenges = [], stackDemands = [] } = tests || {};
  challenges.map(challenge => {
    if (!challengesInfoMapped[challenge.challenge_id]) {
      challengesInfoMapped[challenge.challenge_id] = challenge;
    }
  });

  // First find out tech stacks that don't have any failed tests
  // List all the challenges of those tech stacks
  // Filter out the passed challenges
  // Take the challenge with most YoE
  try {
    const nextChallenge = stackDemands
      .filter(stack => {
        const challenges = stack.groups[0].listMCQ;
        for (let challengeId of challenges) {
          if (
            !challengesInfoMapped[challengeId].passed &&
            challengesInfoMapped[challengeId].state === TestState.COMPLETED
          ) {
            return false;
          }
        }
        return true;
      })
      .reduce((acc, stack) => {
        const challenges = stack.groups[0].listMCQ;
        for (let challenge of challenges) {
          acc = !acc.includes(challenge) ? [...acc, challenge] : acc;
        }
        return acc;
      }, [])
      .map(challengeId => challengesInfoMapped[challengeId])
      .filter(
        ({ state }) =>
          state === TestState.TODO || state === TestState.IN_PROGRESS
      )
      .reduce((highest, current) =>
        highest.YoE > current.YoE ? highest : current
      );

    return nextChallenge;
  } catch (e) {
    return null;
  }
};


export const getTestStackForPassScenario = (tests, mcq_use_apiv1) => {
  const stack = getCurrentActiveStack(tests);
  if (!stack) {
    return [];
  }
  let nextChallengeFromCurrentStack = [];

  if (mcq_use_apiv1) {
    nextChallengeFromCurrentStack = getNextChallengeFromCurrentStackV1(
      stack,
      tests
    );
  } else {
    nextChallengeFromCurrentStack = getNextChallengeFromCurrentStack(
      stack,
      tests
    );
  }

  if (!nextChallengeFromCurrentStack) {
    let nextChallengeFromAllAvailableStack = [];
    if (mcq_use_apiv1) {
      nextChallengeFromAllAvailableStack = getNextChallengeFromAllAvailableStackV1(
        tests
      );
    } else {
      nextChallengeFromAllAvailableStack = getNextChallengeFromAllAvailableStack(
        tests
      );
    }

    if (!nextChallengeFromAllAvailableStack) {
      return [];
    }
    return [stack, nextChallengeFromAllAvailableStack];
  }
  return [stack, nextChallengeFromCurrentStack];
};

export const getTestStackForFailScenario = (tests, mcq_use_apiv1) => {
  const stack = getCurrentActiveStack(tests);

  if (!stack) {
    return [];
  }

  const { challenges = [], stackDemands = [] } = tests;
  if (!stackDemands.length) return [];

  const challengesInfoMapped = {};
  challenges.map(challenge => {
    if (!challengesInfoMapped[challenge.challenge_id]) {
      challengesInfoMapped[challenge.challenge_id] = challenge;
    }
  });

  let challengeFromMostYoEStack = [];

  if (mcq_use_apiv1) {
    challengeFromMostYoEStack = getNextChallengeFromAllAvailableUnfailedStackV1(
      tests
    );
  } else {
    challengeFromMostYoEStack = getNextChallengeFromAllAvailableUnfailedStack(
      tests
    );
  }

  if (challengeFromMostYoEStack) return [stack, challengeFromMostYoEStack];

  let nextChallengeFromCurrentStack = [];

  if (mcq_use_apiv1) {
    nextChallengeFromCurrentStack = getNextChallengeFromCurrentStackV1(
      stack,
      tests
    );
  } else {
    nextChallengeFromCurrentStack = getNextChallengeFromCurrentStack(
      stack,
      tests
    );
  }

  if (!nextChallengeFromCurrentStack) {
    let nextChallengeFromAllAvailableStack = [];

    if (mcq_use_apiv1) {
      nextChallengeFromAllAvailableStack = getNextChallengeFromAllAvailableStackV1(
        tests
      );
    } else {
      nextChallengeFromAllAvailableStack = getNextChallengeFromAllAvailableStack(
        tests
      );
    }

    if (!nextChallengeFromAllAvailableStack) {
      return [];
    }
    return [stack, nextChallengeFromAllAvailableStack];
  }
  return [stack, nextChallengeFromCurrentStack];
};

export const MOD_LOCALSTORAGE_KEY = "previousDevData";

const shouldShowPracticeMcq = (tests, previousDevData, currentDelight) => {
  if (
    tests &&
    tests.practiceMcq &&
    testPassed(tests.practiceMcq) &&
    previousDevData.practiceMcq &&
    !testPassed(previousDevData.practiceMcq || {}) &&
    currentDelight !== "practiceMCQ"
  ) {
    return true;
  }
  return false;
};

export const passedAcc = (home) => {
  const {
    extraPassedInfo = {}
  } = home;
  return extraPassedInfo.passedAcc || false;
};

export const passedApt = (home) => {
  const {
    extraPassedInfo
  } = home;
  return extraPassedInfo && extraPassedInfo.passedApt || false;
};

/**
 * Remove `mcqCheck` post the 100% rollout of  `mcq_use_apiv1` flag
*/
const mcqCheck = (tests, home, previousDevData = {}) => {
  const { mcqInfo = null, firstTechStackPassed = false } = previousDevData;
  if (!mcqInfo) return [false, mcqInfo];
  if (
      home &&
      home.extraPassedInfo &&
      home.extraPassedInfo.passedTechStack &&
      firstTechStackPassed !== undefined &&
      firstTechStackPassed === false)
        return [false, null];

  const { challenges = [] } = tests || {};
  const currentInfo = challenges.find(
    ({ challenge_id }) => challenge_id === mcqInfo.challenge_id
  );
  if (!currentInfo) return [false, null];
  if (
    currentInfo.passed === 1 &&
    mcqInfo.passed !== 1 && !mcqInfo.passModalShown
  )
    return ["pass", currentInfo];
  if (
    currentInfo.passed === 2 &&
    mcqInfo.passed !== 2 &&
    !mcqInfo.failedModalShown
  )
    return ["fail", currentInfo];
  return [false, null];
};

/**
 * Keep `mcqCheckV1` post the 100% rollout of  `mcq_use_apiv1` flag
 */
const mcqCheckV1 = (tests, home, previousDevData = {}) => {
  const { mcqInfo = null, firstTechStackPassed = false } = previousDevData;
  if (!mcqInfo) return [false, mcqInfo];
  if (
    home &&
    home.extraPassedInfo &&
    home.extraPassedInfo.passedTechStack &&
    firstTechStackPassed !== undefined &&
    firstTechStackPassed === false
  )
    return [false, null];

  const { challenges = [] } = tests || {};
  const currentInfo = challenges.find(
    ({ challenge_id }) => challenge_id === mcqInfo.challenge_id
  );
  if (!currentInfo) return [false, null];
  if (currentInfo.passed && !mcqInfo.passed && !mcqInfo.passModalShown)
    return ['pass', currentInfo];
  if (!currentInfo.passed && mcqInfo.passed && !mcqInfo.failedModalShown)
    return ['fail', currentInfo];
  return [false, null];
};

export const changePreviousDevData = (value, key = null) => {
  const currentDevStatus = JSON.parse(
    localStorage.getItem(MOD_LOCALSTORAGE_KEY)
  );
  let valueToSet = null;
  if (key) {
  valueToSet = {
      ...currentDevStatus,
      [key]: value,
    };
  } else {
    valueToSet = value;
  }
  localStorage.setItem(MOD_LOCALSTORAGE_KEY, JSON.stringify(valueToSet));
};

const delightToShow = (home, tests, mcq_use_apiv1) => {
  const [currentDelight, setCurrentDelight] = useState(null);
  const { gb_ui_ux_version_step_2_3 = null } =
    home.LDFlags || {};
  const shouldActivateDelights = gb_ui_ux_version_step_2_3 === "ON";

  const { showTechLeadModal = false, showTechLeadNextStackModel = false } = tests;
  const shouldShowDelights = !showTechLeadModal && !showTechLeadNextStackModel;

  useEffect(() => {
    const previousDevData =
      JSON.parse(localStorage.getItem("previousDevData") || "{}") || {};

    const currentDevStatus = home.devStatus;
    let mcqStatus, mcqInfo;

    if (mcq_use_apiv1) {
      [mcqStatus, mcqInfo] = mcqCheckV1(tests, home, previousDevData);
    } else {
      [mcqStatus, mcqInfo] = mcqCheck(tests, home, previousDevData);
    }

    if (
      home.workExpCompleted &&
      previousDevData.workExpCompleted !== undefined &&
      !previousDevData.workExpCompleted
    ) {
      setCurrentDelight("workExpCompleted");
      changePreviousDevData(home.workExpCompleted, "workExpCompleted");
    }

    else if (shouldShowPracticeMcq(tests, previousDevData, currentDelight)) {
      setCurrentDelight("practiceMCQ");
      changePreviousDevData(tests.practiceMcq, "practiceMcq");
    }

    else if (
      home &&
      home.extraPassedInfo &&
      home.extraPassedInfo.passed1stMCQ === true &&
      previousDevData.firstTestPassed !== undefined &&
      previousDevData.firstTestPassed === false
    ) {
      setCurrentDelight("firstTestPassed");
      changePreviousDevData(true, "firstTestPassed");
      changePreviousDevData({ ...mcqInfo, failedModalShown: true }, "mcqInfo");
    } 
    
    else if (
      home &&
      home.extraPassedInfo &&
      home.extraPassedInfo.passedTechStack === true &&
      previousDevData.firstTechStackPassed !== undefined &&
      previousDevData.firstTechStackPassed === false &&
      tests.isProgrammerRole
    ) {
      setCurrentDelight("firstTechStackPassed");
      changePreviousDevData(currentDevStatus, "devStatus");
      changePreviousDevData(true, "firstTechStackPassed");
      changePreviousDevData({ ...mcqInfo, passModalShown: true }, "mcqInfo")
    }

    else if (mcqStatus === "pass") {
      setCurrentDelight("testPassed");
      changePreviousDevData({ ...mcqInfo, passModalShown: true }, "mcqInfo")
    }

    else if (mcqStatus === "fail") {
      setCurrentDelight("failedMcq");
      changePreviousDevData({ ...mcqInfo, failedModalShown: true }, "mcqInfo");
    }


    else if (
      home &&
      home.extraPassedInfo &&
      home.extraPassedInfo.passedPracticeAcc &&
      previousDevData.passedPracticeAcc !== undefined &&
      !previousDevData.passedPracticeAcc
    ) {
      setCurrentDelight("practiceAccPassed");
      changePreviousDevData(
        home.extraPassedInfo.passedPracticeAcc,
        "passedPracticeAcc"
      );
    }

    else if (
      passedAcc(home) &&
      previousDevData.passedAcc !== undefined &&
      !previousDevData.passedAcc
      && tests.isProgrammerRole
    ) {
      changePreviousDevData(true, "passedAcc");
    }

    else if (
      passedApt(home) &&
      previousDevData.passedApt !== undefined &&
      !previousDevData.passedApt
    ) {
      setCurrentDelight("aptPassed");
      changePreviousDevData(true, "passedApt");
    }

    else if (
      tests.turingProfileCompleted &&
      previousDevData.turingProfileCompleted !== undefined &&
      !previousDevData.turingProfileCompleted
    ) {
      setCurrentDelight("completedProfile");
      changePreviousDevData(
        tests.turingProfileCompleted,
        "turingProfileCompleted"
      );
    }
  }, [home, tests]);

  return shouldActivateDelights && shouldShowDelights ? currentDelight : false;
};

export { delightToShow };
